import React from "react";
import Axios from "axios";
import { BlockSquare } from "../../components/common/StyledComponents";
import {
  antdAsyncMessageError,
  antdAsyncMessageLoading,
  antdAsyncMessageSuccess,
} from "../../helpers/AsyncMessage";
import {
  antdMessageError,
  buildErrorObject,
  errorHandler,
  prettyDateFormat,
} from "../../helpers/Utils";
import { DEFAULT_IMUM_PER_PAGE } from "../../helpers/default";

export const getImums =
  (page, filterParams, type = "Imum") =>
  async (dispatch, getState) => {
    let cancel;
    try {
      dispatch({ type: "ONLY_LOADING_RETAIL" });
      const func_api_call_time = Date.now();
      const { data } = await Axios({
        method: "GET",
        url: `/api/v1/retail_products.json?page=${page}&per_page=${
          type == "Imum" ? DEFAULT_IMUM_PER_PAGE : 9999
        }${filterParams}`,
        cancelToken: new Axios.CancelToken((c) => (cancel = c)),
      });
      const full_data =
        page == 1 ? data.data : [...getState().retail.data, ...data.data];
      const full_data_ids = [...new Set(full_data.map((d) => d.id))];
      const full_filtered = full_data_ids.map((d) =>
        full_data.find((s) => s.id == d),
      );

      dispatch(
        setValue(
          {
            type: "GET_RETAIL_PRODUCTS",
            payload: {
              ...data,
              api_call_time: func_api_call_time,
              data: full_filtered,
              current: type,
              show_data: {
                last_sync: data.last_sync
                  ? prettyDateFormat(data.last_sync)
                  : "",
                progress: data.progress,
                last_sync_priority: data.last_sync_priority
                  ? prettyDateFormat(data.last_sync_priority)
                  : "",
                progress_priority: data.progress_priority,
              },
            },
          },
          func_api_call_time,
        ),
      );
      return {
        success: true,
        has_more:
          data?.data?.length == 0
            ? false
            : data?.data?.length % DEFAULT_IMUM_PER_PAGE == 0,
      };
    } catch (err) {
      errorHandler(err);
      if (Axios.isCancel(err)) return;
      antdMessageError();
      return { success: false };
    }
  };

export const getImumsOptions = () => async (dispatch) => {
  try {
    const [imum_shops_res, imum_product_tags_res, retail_rules_res] =
      await Promise.all([
        Axios.get(`/api/v1/retail_product_rules/retail_competitors`).catch(
          () => ({ data: { data: [] } }),
        ),
        Axios.get(`/api/v1/retail_product_tags`),
        Axios.get(`/api/v1/retail_product_rules/options`).catch(
          () => ({ data: [] }),
        ),
      ]);
    const { data: imum_product_tags } = imum_product_tags_res;
    const { data: imum_rules = [] } = retail_rules_res;
    const shops = (imum_shops_res.data?.data || []).map((shop) => ({
      value: shop,
      label: shop,
    }));

    dispatch({
      type: "SET_RETAIL_OPTIONS",
      payload: {
        shops,
        imum_product_tags: imum_product_tags.map((d) => ({
          value: d.id,
          label: d.title,
          color: d.color,
          preview: (
            <div className="d-flex align-items-center">
              <div className="mr-1">
                <BlockSquare color={d.color} />
              </div>
              {d.title}
            </div>
          ),
        })),
        imum_rules,
      },
    });
    return { success: true };
  } catch (err) {
    console.log("ERROR ", err);
    return { success: false };
  }
};

export const rollbackImumToPim = async (id) => {
  try {
    const { data } = await Axios.post(
      `/api/v1/retail_products/rollback_pim?id=${id}`,
    );
    antdAsyncMessageLoading({ key: "export_to_pim" });

    if (!data.success) throw new Error("ERR");
    antdAsyncMessageSuccess({ key: "export_to_pim" });
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "export_to_pim" });
    return { success: false };
  }
};

export const exportImumToAlso = (lang, type, ids, filterString) => async () => {
  try {
    antdAsyncMessageLoading({ key: "export_to_pim", text: "Sent to import" });

    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/export_to_pim?${filterString}`,
      {
        all: type == "all" ? true : false,
        ids,
        lang: lang.length == 1 && lang[0] == "LT" ? ["TCH_RETAIL"] : lang,
      },
    );
    if (!data.success) throw new Error("ERR");

    setTimeout(() => {
      antdAsyncMessageSuccess({ key: "export_to_pim" });
    }, 3000);
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "export_to_pim" });

    return { success: false };
  }
};

export const exportImumToRivile =
  (departments, type, ids, filterString = "") =>
  async () => {
    try {
      antdAsyncMessageLoading({
        key: "export_to_rivile",
        text: "Queued for Rivile export",
      });

      const { data } = await Axios.post(
        `/api/v1/retail_products/export_to_rivile?${filterString}`,
        {
          all: type == "all" ? true : false,
          ids,
          departments,
        },
      );
      if (!data.success) throw new Error("ERR");

      setTimeout(() => {
        antdAsyncMessageSuccess({ key: "export_to_rivile" });
      }, 1000);
      return { success: true };
    } catch (err) {
      errorHandler(err);
      antdAsyncMessageError({ key: "export_to_rivile" });

      return { success: false };
    }
  };

export const imumMassChangePriceToOldPrice = async (departments, ids) => {
  const key = "mass_change_price";
  try {
    antdAsyncMessageLoading({ key });
    const { data } = await Axios.post(
      `/api/v1/retail_products/mass_change_price`,
      {
        ids,
        departments,
      },
    );
    if (!data.success) throw new Error("ERR");
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const imumDisableChannelPriceScope = async (lang, ids) => {
  const key = "disable_channel_price_scope";
  try {
    antdAsyncMessageLoading({ key });
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/disable_channel_price_scope`,
      {
        ids,
        lang,
      },
    );
    if (!data.success) throw new Error("Operation failed");
    if (data.success) antdAsyncMessageSuccess({ key });
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const imumDisableChannelPriceScopeParams = async (lang, queryString) => {
  const key = "disable_channel_price_scope";

  const transformQuery = (queryStr) => {
    const cleanStr = queryStr.startsWith("&")
      ? queryStr.substring(1)
      : queryStr;

    const params = {};
    const matches = cleanStr.match(/q\[([^\]]+)\](\[\])?=([^&]+)/g);

    if (matches) {
      matches.forEach((match) => {
        const [_, param, isArray, value] = match.match(
          /q\[([^\]]+)\](\[\])?=([^&]+)/,
        );
        if (isArray) {
          if (!params[param]) {
            params[param] = [];
          }
          params[param].push(value.toLowerCase());
        } else {
          params[param] = value.toLowerCase();
        }
      });
    }

    return { q: params };
  };

  try {
    antdAsyncMessageLoading({ key });
    const transformedQuery = transformQuery(queryString);
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/disable_channel_price_scope`,
      {
        lang,
        ...transformedQuery,
      },
    );

    if (!data.success) throw new Error("Operation failed");
    if (data.success) antdAsyncMessageSuccess({ key });
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const imumMassNewPriceChangeByCountries = async (
  filterString,
  variables,
) => {
  const key = "mass_new_price_change_by_countries";
  try {
    antdAsyncMessageLoading({ key });
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/mass_new_price_change_by_countries?${filterString}`,
      variables,
    );
    if (!data.success) throw new Error("ERR");
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const getImumXls = async (params) => {
  try {
    antdAsyncMessageLoading({
      key: "exportDocument",
      text: <span id="imum_xlsx_progress">In progress...</span>,
    });
    const { data } = await Axios.post(
      `/api/v1/retail_products/get_xls?${params?.filter || ""}`,
      params,
    );
    if (!data.success) throw new Error("ERR");
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "exportDocument" });
  }
};

export const putRetailPrices =
  (parent_id, pricesAttributes) => async (dispatch, getState) => {
    try {
      antdAsyncMessageLoading({ key: "putImums" });

      const { data: putData } = await Axios({
        url: `/api/v1/retail_products/${parent_id}.json`,
        data: {
          retail_product: {
            prices_attributes: pricesAttributes,
          },
        },
        method: "PUT",
      });

      // PUT response may have stale/partial prices due to Rails association cache;
      // merge updated and newly-created prices into the existing product in Redux state.
      const existing = getState().retail.data.find((d) => d.id === parent_id);
      let payload = putData;
      if (existing && putData.prices && existing.prices) {
        const mergedPrices = [...existing.prices];
        putData.prices.forEach((incomingPrice) => {
          const existingIndex = mergedPrices.findIndex((existingPrice) => {
            if (incomingPrice.id && existingPrice.id) return incomingPrice.id === existingPrice.id;
            return existingPrice.price_type === incomingPrice.price_type &&
              existingPrice.department === incomingPrice.department;
          });

          if (existingIndex >= 0) mergedPrices[existingIndex] = incomingPrice;
          else mergedPrices.push(incomingPrice);
        });
        payload = { ...existing, ...putData, prices: mergedPrices };
      }

      antdAsyncMessageSuccess({ key: "putImums" });
      dispatch({
        type: "UPDATE_RETAIL_PRODUCT",
        payload,
      });
      return { success: true };
    } catch (err) {
      errorHandler(err);
      antdAsyncMessageError({ key: "putImums" });
      return {
        success: false,
        errors: buildErrorObject(err?.response?.data?.errors),
      };
    }
  };

export const putImums =
  (id, imum, parent_id, related_ids) => async (dispatch) => {
    const priceAttrs = { id };
    if (imum.price !== undefined) priceAttrs.price = imum.price;
    if (imum.freeze_from !== undefined)
      priceAttrs.freeze_from = imum.freeze_from;
    if (imum.freeze_to !== undefined) priceAttrs.freeze_to = imum.freeze_to;
    if (imum.base_price !== undefined) {
      priceAttrs.price = imum.base_price;
      priceAttrs.price_type = "new";
    }
    return dispatch(putRetailPrices(parent_id, [priceAttrs]));
  };

export const putImumProduct = (body) => async (dispatch) => {
  try {
    antdAsyncMessageLoading({ key: "putImumProduct" });
    const { data } = await Axios({
      url: `/api/v1/${window.identifier}/imums/${body.id}.json`,
      data: {
        imum_product: body,
      },
      method: "put",
    });
    antdAsyncMessageSuccess({ key: "putImumProduct" });
    console.log({ data });
    dispatch({
      type: "UPDATE_CUSTOM",
      payload: data,
    });
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "putImumProduct" });
    return {
      success: false,
      errors: buildErrorObject(err?.response?.data?.errors),
    };
  }
};

export const putRetailProduct = (body) => async (dispatch) => {
  try {
    antdAsyncMessageLoading({ key: "putRetailProduct" });
    const { data } = await Axios({
      url: `/api/v1/retail_products/${body.id}.json`,
      data: {
        retail_product: body,
      },
      method: "put",
    });
    antdAsyncMessageSuccess({ key: "putRetailProduct" });
    dispatch({
      type: "UPDATE_RETAIL_PRODUCT",
      payload: data,
    });
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "putRetailProduct" });
    return {
      success: false,
      errors: buildErrorObject(err?.response?.data?.errors),
    };
  }
};

export const imumCheckIfHaveDifference = async (params) => {
  try {
    antdAsyncMessageLoading({
      key: "imumCheckIfHaveDifference",
      text: "Check if have prices 15% difference",
    });

    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/check_if_have_15_difference`,
      {
        ...params,
        lang:
          params.lang.length == 1 && params.lang[0] == "LT"
            ? ["TCH_RETAIL"]
            : params.lang,
      },
    );

    if (data.success) {
      antdAsyncMessageSuccess({
        key: "imumCheckIfHaveDifference",
      });
      return data;
    } else {
      antdAsyncMessageSuccess({
        key: "imumCheckIfHaveDifference",
      });
      return data;
    }
    throw new Error("ERR");
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "imumCheckIfHaveDifference" });
    return false;
  }
};

const setValue =
  (val, time = Date.now()) =>
  (dispatch, getState) => {
    const api_call_time = getState().custom.api_call_time;
    console.log("1");
    if (api_call_time > time) return console.log("%cSkipina", "color: red;");
    console.log("3");
    dispatch(val);
  };
